/**
 * Admissions workspace — the staff side of the FWBC pipeline.
 *
 * Pipeline board · applicant register · programme structure · the checklist
 * engine. Every number comes from live data.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ClipboardList, GraduationCap, ListChecks, Search, FileCheck2, Users,
  ShieldCheck, Plus, Trash2, Download, BarChart3, PhoneCall, Plane, DoorOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import AdmissionsDecisionPanel from '@/components/school/AdmissionsDecisionPanel';
import AdmissionsJourneyPanel, { AdmissionsQueues } from '@/components/school/AdmissionsJourneyPanel';
import AdmissionsReports from '@/components/school/AdmissionsReports';
import AdmissionsEnquiries from '@/components/school/AdmissionsEnquiries';
import { AdmissionsPaymentSettings } from '@/components/school/AdmissionsFeeSettings';
import ArrivalDayPanel from '@/components/school/ArrivalDayPanel';

const TABS = [
  { id: 'pipeline', label: 'Pipeline', icon: ClipboardList },
  { id: 'decisions', label: 'Decisions', icon: FileCheck2 },
  { id: 'register', label: 'Applicants', icon: Users },
  { id: 'programmes', label: 'Programmes', icon: GraduationCap },
  { id: 'requirements', label: 'Requirements', icon: ListChecks },
  { id: 'enquiries', label: 'Enquiries', icon: PhoneCall },
  { id: 'international', label: 'International & rooms', icon: Plane },
  { id: 'arrival', label: 'Arrival day', icon: DoorOpen },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-slate-100 text-slate-600',
  submitted: 'bg-sky-100 text-sky-700',
  incomplete: 'bg-amber-100 text-amber-800',
  under_review: 'bg-indigo-100 text-indigo-700',
  interview: 'bg-violet-100 text-violet-700',
  decision_pending: 'bg-fuchsia-100 text-fuchsia-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  conditional: 'bg-teal-100 text-teal-700',
  declined: 'bg-rose-100 text-rose-700',
  deferred: 'bg-orange-100 text-orange-800',
  withdrawn: 'bg-slate-200 text-slate-600',
};

const pretty = (s) => String(s || '').replace(/_/g, ' ');

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-lg border bg-white px-3 py-2" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-xl font-semibold text-slate-900">{value}</p>
  </div>
);

const PipelineBoard = ({ tenantId, onOpen }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['admissions-pipeline', tenantId],
    queryFn: async () => (await api.get(`/school/admissions/tenants/${tenantId}/pipeline`)).data,
  });
  if (isLoading || !data) return <Skeleton className="h-40 w-full" />;
  const k = data.kpis;
  return (
    <div className="space-y-4" data-testid="admissions-pipeline">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Applications" value={k.applications} testid="kpi-applications" />
        <Kpi label="Submitted" value={k.submitted} testid="kpi-submitted" />
        <Kpi label="Ready for review" value={k.ready_for_review} testid="kpi-review" />
        <Kpi label="Accepted" value={k.accepted} testid="kpi-accepted" />
        <Kpi label="Registered" value={k.registered} testid="kpi-registered" />
        <Kpi label="Acceptance rate" value={`${k.acceptance_rate}%`} testid="kpi-acceptance" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.stages.map((s) => (
          <button key={s.key} onClick={() => onOpen(s.key)}
                  data-testid={`pipeline-stage-${s.key}`}
                  className="text-left rounded-lg border bg-white px-3 py-3 hover:border-slate-400 transition-colors">
            <p className="text-2xl font-bold text-slate-900">{s.count}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">
        {k.international} international · {k.online} online · {k.finance_pending} awaiting
        finance clearance · registration rate {k.registration_rate}%
      </p>
    </div>
  );
};

const ApplicationDrawer = ({ tenantId, applicationId, onClose }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admissions-application', tenantId, applicationId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}`)).data,
    enabled: !!applicationId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admissions-application', tenantId, applicationId] });
    qc.invalidateQueries({ queryKey: ['admissions-register', tenantId] });
    qc.invalidateQueries({ queryKey: ['admissions-pipeline', tenantId] });
  };

  const move = useMutation({
    mutationFn: (status) => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/status`,
      { status }),
    onSuccess: () => { toast.success('Stage updated'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not move that on'),
  });

  const reviewDoc = useMutation({
    mutationFn: ({ id, status }) => api.post(
      `/school/admissions/tenants/${tenantId}/documents/${id}/review`, { status }),
    onSuccess: () => { toast.success('Document updated'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not update it'),
  });

  return (
    <Dialog open={!!applicationId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid="application-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {data?.applicant_name || 'Applicant'}
            {data && (
              <Badge className={`${STATUS_TONE[data.status] || ''} capitalize`}
                     data-testid="application-status">{pretty(data.status)}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        {!data ? <Skeleton className="h-48 w-full" /> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
              <span><strong>{data.application_number}</strong></span>
              <span>{data.programme_name || '—'}</span>
              <span>{data.intake_name || 'No intake chosen'}</span>
              <span className="capitalize">
                {pretty(data.applicant_type)} · {pretty(data.study_mode)}
              </span>
              <span>{data.applicant_email}</span>
              <span>Enrolment: {pretty(data.enrolment_status)}</span>
            </div>

            <div>
              <p className="font-semibold mb-1 flex items-center gap-1">
                <ListChecks className="h-4 w-4 text-indigo-600" /> Checklist
                <span className="text-xs text-slate-500 font-normal">
                  {data.checklist_summary.satisfied} of {data.checklist_summary.required} done
                </span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {(data.checklist || []).map((i) => (
                  <div key={i.code} className="flex items-center gap-2 text-xs"
                       data-testid={`checklist-${i.code}`}>
                    <Badge variant="outline" className="capitalize text-[10px]">
                      {pretty(i.status)}
                    </Badge>
                    <span className={i.required ? '' : 'text-slate-400'}>{i.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="font-semibold mb-1 flex items-center gap-1">
                <FileCheck2 className="h-4 w-4 text-indigo-600" /> Documents
              </p>
              {!(data.documents || []).length ? (
                <p className="text-xs text-slate-500">Nothing uploaded yet.</p>
              ) : data.documents.map((d) => (
                <div key={d.id} className="flex items-center gap-2 py-1 flex-wrap"
                     data-testid={`document-${d.id}`}>
                  <a href={d.url} target="_blank" rel="noreferrer"
                     className="text-xs text-indigo-600 hover:underline">{d.label}</a>
                  <Badge variant="outline" className="capitalize text-[10px]">
                    {pretty(d.status)}
                  </Badge>
                  {['verified', 'rejected', 'reupload_requested', 'waived'].map((s) => (
                    <button key={s} onClick={() => reviewDoc.mutate({ id: d.id, status: s })}
                            data-testid={`document-${d.id}-${s}`}
                            className="text-[11px] text-slate-500 hover:text-slate-900 capitalize">
                      {pretty(s)}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div>
              <p className="font-semibold mb-1 flex items-center gap-1">
                <ShieldCheck className="h-4 w-4 text-indigo-600" /> References
              </p>
              {!(data.references || []).length ? (
                <p className="text-xs text-slate-500">None requested yet.</p>
              ) : data.references.map((r) => (
                <div key={r.id} className="text-xs py-1" data-testid={`reference-${r.id}`}>
                  <span className="capitalize">{pretty(r.reference_type)}</span> ·{' '}
                  {r.referee_name} · <span className="capitalize">{pretty(r.status)}</span>
                  {r.content?.comments && (
                    <p className="text-slate-600 mt-0.5 bg-slate-50 rounded px-2 py-1">
                      <strong className="capitalize">{r.content.recommendation}</strong> —{' '}
                      {r.content.comments}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap border-t pt-3">
              {['under_review', 'interview', 'decision_pending'].map((s) => (
                <Button key={s} size="sm" variant="outline" className="h-7 text-xs capitalize"
                        data-testid={`move-${s}`} disabled={move.isPending}
                        onClick={() => move.mutate(s)}>
                  Move to {pretty(s)}
                </Button>
              ))}
            </div>

            <AdmissionsDecisionPanel tenantId={tenantId} application={data}
                                     invalidate={invalidate} />

            <AdmissionsJourneyPanel tenantId={tenantId} application={data} />

            {(data.history || []).slice(-4).reverse().map((h, i) => (
              <p key={i} className="text-[11px] text-slate-500" data-testid={`history-${i}`}>
                {h.at?.slice(0, 16).replace('T', ' ')} · {pretty(h.from_status) || '—'} →{' '}
                {pretty(h.to_status) || pretty(h.to_enrolment)} · {h.by_email || 'system'}
                {h.reason ? ` · ${h.reason}` : ''}
              </p>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DecisionQueue = ({ tenantId, onOpen }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['admissions-decision-queue', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/decision-queue`)).data,
  });
  if (isLoading || !data) return <Skeleton className="h-40 w-full" />;

  const groups = [
    ['ready_for_review', 'Ready for review'],
    ['interview', 'Interview'],
    ['awaiting_decision', 'Awaiting decision'],
    ['conditional', 'Conditional — waiting on conditions'],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admissions-decisions">
      {groups.map(([key, label]) => (
        <Card key={key} data-testid={`queue-${key}`}>
          <CardHeader className="py-3"><CardTitle className="text-base">
            {label} <span className="text-slate-400">({data[key].length})</span>
          </CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {!data[key].length ? (
              <p className="text-xs text-slate-500">Nothing here.</p>
            ) : data[key].map((a) => (
              <button key={a.id} onClick={() => onOpen(a.id)}
                      data-testid={`queue-row-${a.id}`}
                      className="w-full text-left text-xs hover:bg-slate-50 rounded px-2 py-1">
                <span className="font-medium">{a.applicant_name || '—'}</span>
                <span className="text-slate-400"> · {a.application_number}</span>
                <span className="block text-slate-500">
                  {a.reviews} review{a.reviews === 1 ? '' : 's'} ·{' '}
                  {a.checklist.satisfied}/{a.checklist.required} checklist
                  {a.outstanding_conditions
                    ? ` · ${a.outstanding_conditions} condition(s) outstanding` : ''}
                </span>
              </button>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Register = ({ tenantId, status, setStatus, onOpen }) => {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['admissions-register', tenantId, status, search],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications`,
      { params: { status: status || undefined, search: search || undefined } })).data,
  });

  return (
    <div className="space-y-3" data-testid="admissions-register">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1 min-w-0 sm:min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-10" placeholder="Name, email, application or student number"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="register-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="register-status-filter"
                className="h-9 rounded-md border border-slate-200 px-2 text-sm capitalize">
          <option value="">Every status</option>
          {(data?.statuses || []).map((s) => (
            <option key={s} value={s}>{pretty(s)}</option>
          ))}
        </select>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/school/admissions/tenants/${tenantId}/applications.csv`}
           className="ml-auto text-xs text-indigo-600 hover:underline"
           data-testid="register-export">
          <Download className="h-3 w-3 inline mr-1" />Export CSV
        </a>
      </div>
      {isLoading ? <Skeleton className="h-32 w-full" /> : !(data?.items || []).length ? (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500"
                            data-testid="register-empty">
          No applications match that.
        </CardContent></Card>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Applicant</TableHead><TableHead>Application</TableHead>
            <TableHead>Programme</TableHead><TableHead>Type</TableHead>
            <TableHead>Checklist</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((a) => (
              <TableRow key={a.id} className="cursor-pointer"
                        data-testid={`register-row-${a.id}`}
                        onClick={() => onOpen(a.id)}>
                <TableCell>
                  <p className="font-medium">{a.applicant_name || '—'}</p>
                  <p className="text-xs text-slate-500">{a.applicant_email}</p>
                </TableCell>
                <TableCell className="text-xs">{a.application_number}</TableCell>
                <TableCell className="text-xs">{a.programme_name || '—'}</TableCell>
                <TableCell className="text-xs capitalize">{pretty(a.applicant_type)}</TableCell>
                <TableCell className="text-xs">
                  {a.checklist_summary.satisfied}/{a.checklist_summary.required}
                </TableCell>
                <TableCell>
                  <Badge className={`${STATUS_TONE[a.status] || ''} capitalize`}>
                    {pretty(a.status)}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const Programmes = ({ tenantId }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ code: '', name: '', award: 'diploma', duration_years: 2 });
  const { data } = useQuery({
    queryKey: ['admissions-programmes', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/programmes`)).data,
  });

  const create = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/programmes`, {
      ...form, duration_years: Number(form.duration_years) || 1 }),
    onSuccess: () => {
      toast.success('Programme added');
      setForm({ code: '', name: '', award: 'diploma', duration_years: 2 });
      qc.invalidateQueries({ queryKey: ['admissions-programmes', tenantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not add that'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/school/tenants/${tenantId}/programmes/${id}`),
    onSuccess: () => {
      toast.success('Programme removed');
      qc.invalidateQueries({ queryKey: ['admissions-programmes', tenantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not remove it'),
  });

  return (
    <div className="space-y-3" data-testid="admissions-programmes">
      <Card><CardHeader className="py-3">
        <CardTitle className="text-base">Add a programme</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <Input placeholder="Code (DIPMIN)" value={form.code}
               onChange={(e) => setForm({ ...form, code: e.target.value })}
               data-testid="programme-code" />
        <Input placeholder="Diploma in Ministry" value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="md:col-span-2" data-testid="programme-name" />
        <select value={form.award} data-testid="programme-award"
                onChange={(e) => setForm({ ...form, award: e.target.value })}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm capitalize">
          {['certificate', 'diploma', 'degree', 'ministry', 'hexco', 'short_course'].map((a) => (
            <option key={a} value={a}>{pretty(a)}</option>
          ))}
        </select>
        <Button onClick={() => create.mutate()} disabled={create.isPending}
                data-testid="programme-save">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </CardContent></Card>

      {!(data?.items || []).length ? (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500"
                            data-testid="programmes-empty">
          No programmes yet — add the first one above and applicants can start applying.
        </CardContent></Card>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Programme</TableHead>
            <TableHead>Award</TableHead><TableHead className="text-right">Levels</TableHead>
            <TableHead className="text-right">Modules</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((p) => (
              <TableRow key={p.id} data-testid={`programme-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.code}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell className="capitalize text-xs">{pretty(p.award)}</TableCell>
                <TableCell className="text-right">{p.levels}</TableCell>
                <TableCell className="text-right">{p.modules}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-rose-600"
                          data-testid={`programme-delete-${p.id}`}
                          onClick={() => remove.mutate(p.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const Requirements = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admissions-requirements', tenantId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/requirements`)).data,
  });

  const seed = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/requirements/seed-defaults`),
    onSuccess: (r) => {
      toast.success(`${r.data.created} requirement(s) added`);
      qc.invalidateQueries({ queryKey: ['admissions-requirements', tenantId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not seed those'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(
      `/school/admissions/tenants/${tenantId}/requirements/${id}`),
    onSuccess: () => {
      toast.success('Requirement removed');
      qc.invalidateQueries({ queryKey: ['admissions-requirements', tenantId] });
    },
  });

  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="admissions-requirements">
      <div className="flex items-center gap-2">
        <p className="text-sm text-slate-600">
          What each applicant must provide. Scope a rule to a programme, campus,
          applicant type, study mode, country or academic year — anything left empty
          applies to everybody.
        </p>
        <Button size="sm" variant="outline" className="ml-auto whitespace-nowrap"
                data-testid="requirements-seed" disabled={seed.isPending}
                onClick={() => seed.mutate()}>
          Add the standard set
        </Button>
      </div>
      {!items.length ? (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500"
                            data-testid="requirements-empty">
          No requirements yet — start with the standard set and adjust from there.
        </CardContent></Card>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Requirement</TableHead><TableHead>Kind</TableHead>
            <TableHead>Applies to</TableHead><TableHead>Required</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {items.map((r) => {
              const scope = r.applies_to || {};
              const bits = [
                ...(scope.applicant_types || []).map(pretty),
                ...(scope.study_modes || []).map(pretty),
                ...(scope.countries || []),
              ];
              return (
                <TableRow key={r.id} data-testid={`requirement-row-${r.code}`}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell className="capitalize text-xs">{pretty(r.kind)}</TableCell>
                  <TableCell className="text-xs capitalize">
                    {bits.length ? bits.join(', ') : 'Everyone'}
                  </TableCell>
                  <TableCell className="text-xs">{r.required ? 'Yes' : 'Optional'}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-7 text-rose-600"
                            data-testid={`requirement-delete-${r.code}`}
                            onClick={() => remove.mutate(r.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export const AdmissionsWorkspace = ({ tenantId }) => {
  const [tab, setTab] = useState('pipeline');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState(null);

  const openStage = (key) => { setStatus(key); setTab('register'); };

  return (
    <div className="space-y-4" data-testid="admissions-workspace">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Admissions</h2>
        <span className="text-xs text-slate-500">
          Enquiry → application → review → decision → registration
        </span>
      </div>

      <div className="flex rounded-md border border-slate-200 overflow-hidden bg-white w-fit">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`admissions-tab-${t.id}`}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                    tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineBoard tenantId={tenantId} onOpen={openStage} />}
      {tab === 'decisions' && <DecisionQueue tenantId={tenantId} onOpen={setOpenId} />}
      {tab === 'register' && (
        <Register tenantId={tenantId} status={status} setStatus={setStatus}
                  onOpen={setOpenId} />
      )}
      {tab === 'programmes' && <Programmes tenantId={tenantId} />}
      {tab === 'requirements' && (
        <div className="space-y-4">
          <AdmissionsPaymentSettings tenantId={tenantId} />
          <Requirements tenantId={tenantId} />
        </div>
      )}
      {tab === 'enquiries' && <AdmissionsEnquiries tenantId={tenantId} />}
      {tab === 'international' && (
        <AdmissionsQueues tenantId={tenantId} onOpen={setOpenId} />
      )}
      {tab === 'arrival' && <ArrivalDayPanel tenantId={tenantId} />}
      {tab === 'reports' && <AdmissionsReports tenantId={tenantId} />}

      {openId && (
        <ApplicationDrawer tenantId={tenantId} applicationId={openId}
                           onClose={() => setOpenId(null)} />
      )}
    </div>
  );
};

export default AdmissionsWorkspace;
